import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Pagination,
  CircularProgress, Button, Tooltip, Alert, Skeleton,
} from "@mui/material";
import CheckCircleRoundedIcon  from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon       from "@mui/icons-material/CancelRounded";
import PlayArrowRoundedIcon    from "@mui/icons-material/PlayArrowRounded";
import PeopleAltRoundedIcon    from "@mui/icons-material/PeopleAltRounded";
import InboxRoundedIcon        from "@mui/icons-material/InboxRounded";
import WifiRoundedIcon         from "@mui/icons-material/WifiRounded";
import FlagRoundedIcon         from "@mui/icons-material/FlagRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import TaskAltRoundedIcon      from "@mui/icons-material/TaskAltRounded";
import VerifiedRoundedIcon     from "@mui/icons-material/VerifiedRounded";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt:"#FFF0E0",
  amber:   "#D97706", amberLt: "#FEF3C7",
  emerald: "#059669", emeraldLt:"#D1FAE5",
  rose:    "#E11D48", roseLt:  "#FFE4E6",
  indigo:  "#4F46E5", indigoLt:"#EEF2FF",
  bg:      "#F7FAFC", border:  "#E2E8F0",
  text:    "#0F172A", muted:   "#64748B",
};

const STATUS_META = {
  pending:    { bg:T.amberLt,    color:T.amber,   label:"Pending",    icon:<HourglassTopRoundedIcon /> },
  qualifying: { bg:T.indigoLt,   color:T.indigo,  label:"Qualifying", icon:<VerifiedRoundedIcon /> },
  paid:       { bg:T.emeraldLt,  color:T.emerald, label:"Paid",       icon:<TaskAltRoundedIcon /> },
  flagged:    { bg:T.roseLt,     color:T.rose,    label:"Flagged",    icon:<FlagRoundedIcon /> },
};

const FILTERS = [
  { value:"flagged",    label:"Flagged",    color:T.rose,    bg:T.roseLt    },
  { value:"pending",    label:"Pending",    color:T.amber,   bg:T.amberLt   },
  { value:"qualifying", label:"Qualifying", color:T.indigo,  bg:T.indigoLt  },
  { value:"paid",       label:"Paid",       color:T.emerald, bg:T.emeraldLt },
  { value:"",           label:"All",        color:T.muted,   bg:T.border    },
];

const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <Chip size="small" label={m.label}
      icon={React.cloneElement(m.icon, { style:{ fontSize:12, color:m.color, marginLeft:6 } })}
      sx={{ bgcolor:m.bg,color:m.color,fontWeight:700,fontSize:11,height:24,
            border:`1px solid ${m.color}33`,
            "& .MuiChip-icon":{ color:m.color } }} />
  );
};

const UserBadge = ({ type, id }) => (
  <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.5,
    px:1,py:0.3,borderRadius:"8px",
    bgcolor: type==="owner" ? T.indigoLt : T.tealLt,
    border:`1px solid ${type==="owner" ? T.indigo : T.teal}22` }}>
    <Typography sx={{ fontSize:11,fontWeight:700,color:type==="owner" ? T.indigo : T.teal,textTransform:"uppercase",letterSpacing:"0.3px" }}>
      {type}
    </Typography>
    <Typography sx={{ fontSize:12,fontWeight:600,color:T.text }}>#{id}</Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────
export default function ReferralFraudQueue() {
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [statusFilter, setStatusFilter] = useState("flagged");
  const [busyId,       setBusyId]       = useState(null);
  const [sweepRunning, setSweepRunning] = useState(false);
  const [sweepSummary, setSweepSummary] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:25 };
      if (statusFilter) params.status = statusFilter;
      const res = await getData(API_ENDPOINTS.dimReferrals, params);
      const r = res?.result;
      if (r) { setRows(r.rows || []); setTotalPages(r.totalPages || 1); }
    } catch(e) { console.error("Referrals fetch:", e); }
    setLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const action = async (id, kind) => {
    setBusyId(id);
    try {
      const url = kind === "approve"
        ? `/api/dim/admin/referrals/${id}/approve`
        : `/api/dim/admin/referrals/${id}/reject`;
      await postData(url, kind === "reject" ? { reason:"admin rejected via UI" } : {});
      await fetchRows();
    } catch(e) { alert(e.response?.data?.message || `${kind} failed`); }
    finally { setBusyId(null); }
  };

  const runSweep = async () => {
    setSweepRunning(true); setSweepSummary(null);
    try {
      const res = await postData(API_ENDPOINTS.dimReferralSweepNow, {});
      setSweepSummary(res?.result); await fetchRows();
    } catch(e) { alert(e.response?.data?.message || "Sweep failed"); }
    finally { setSweepRunning(false); }
  };

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <PeopleAltRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Referrals
            </Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
              DIM-001 · Customer &amp; owner referrals · Fraud queue
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" disableElevation
          startIcon={sweepRunning ? <CircularProgress size={14} sx={{ color:"inherit" }} /> : <PlayArrowRoundedIcon sx={{ fontSize:16 }} />}
          onClick={runSweep} disabled={sweepRunning}
          sx={{ borderRadius:"12px",textTransform:"none",fontWeight:700,
            bgcolor:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",
            "&:hover":{ bgcolor:"rgba(255,255,255,0.3)" },
            "&.Mui-disabled":{ bgcolor:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.35)" } }}>
          {sweepRunning ? "Sweeping…" : "Run Fraud Sweep"}
        </Button>
      </Box>

      {/* ── Sweep result ── */}
      {sweepSummary && (
        <Alert severity="success" variant="filled" onClose={()=>setSweepSummary(null)}
          sx={{ borderRadius:"12px",mb:2 }}>
          Sweep complete — <strong>{sweepSummary.evaluated}</strong> pending referrals evaluated,{" "}
          <strong>{sweepSummary.flagged}</strong> newly flagged.
        </Alert>
      )}

      {/* ── Filter pills ── */}
      <Box sx={{ display:"flex",gap:1,mb:2.5,flexWrap:"wrap" }}>
        {FILTERS.map(f => {
          const active = statusFilter === f.value;
          return (
            <Box key={f.value} onClick={()=>{ setStatusFilter(f.value); setPage(1); }}
              sx={{ px:1.5,py:0.6,borderRadius:"10px",cursor:"pointer",fontWeight:700,fontSize:13,
                bgcolor: active ? f.color : "#fff",
                color:   active ? "#fff"   : f.color,
                border:`1.5px solid ${active ? f.color : f.color+"55"}`,
                transition:"all 0.15s",
                "&:hover":{ bgcolor: active ? f.color : f.bg },
              }}>
              {f.label}
            </Box>
          );
        })}
      </Box>

      {/* ── Table card ── */}
      <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
        <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />

        {loading ? (
          <Box sx={{ p:2.5 }}>
            {Array.from({length:4}).map((_,i) => (
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb:1,borderRadius:"10px" }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py:8,textAlign:"center" }}>
            <Box sx={{ width:60,height:60,borderRadius:"16px",bgcolor:T.tealLt,display:"inline-flex",alignItems:"center",justifyContent:"center",mb:2 }}>
              <InboxRoundedIcon sx={{ fontSize:30,color:T.teal }} />
            </Box>
            <Typography sx={{ fontSize:15,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              No referrals match this filter
            </Typography>
            <Typography sx={{ fontSize:13,color:T.muted,mt:0.5 }}>
              {statusFilter === "flagged"
                ? "No fraud-flagged referrals — clean state."
                : "Try a different filter or switch to All."}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root":{
                  bgcolor:"#F8FAFC", fontWeight:700, fontSize:11,
                  textTransform:"uppercase", letterSpacing:"0.4px", color:T.muted,
                  py:1.25, borderBottom:`2px solid ${T.border}`,
                }}}>
                  <TableCell>When</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Referrer</TableCell>
                  <TableCell>Referee</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Flag reason</TableCell>
                  <TableCell>Amounts</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.referral_id} hover sx={{ "&:hover":{ bgcolor:"#F8FAFC" },"& .MuiTableCell-root":{ py:1.5,borderBottom:`1px solid ${T.border}` } }}>

                    <TableCell>
                      <Typography sx={{ fontSize:12,color:T.text,fontWeight:500 }}>
                        {new Date(r.createdAt).toLocaleDateString("en-IN")}
                      </Typography>
                      <Typography sx={{ fontSize:11,color:T.muted }}>
                        {new Date(r.createdAt).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ px:1,py:0.4,bgcolor:T.tealLt,borderRadius:"7px",display:"inline-block",border:`1px solid ${T.teal}22` }}>
                        <Typography sx={{ fontFamily:"monospace",fontSize:12,fontWeight:700,color:T.teal }}>
                          {r.referral_code}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell><UserBadge type={r.referrer_user_type} id={r.referrer_user_id} /></TableCell>
                    <TableCell><UserBadge type={r.referee_user_type}  id={r.referee_user_id}  /></TableCell>

                    <TableCell><StatusChip status={r.status} /></TableCell>

                    <TableCell>
                      {r.signup_ip ? (
                        <Box sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
                          <WifiRoundedIcon sx={{ fontSize:13,color:T.muted }} />
                          <Typography sx={{ fontSize:11,fontFamily:"monospace",color:T.muted }}>{r.signup_ip}</Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize:11,color:T.border }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ maxWidth:160 }}>
                      {r.flagged_reason ? (
                        <Box sx={{ px:1,py:0.3,bgcolor:T.roseLt,borderRadius:"7px",border:`1px solid ${T.rose}22` }}>
                          <Typography sx={{ fontSize:11,color:T.rose,fontWeight:600,lineHeight:1.4 }}>{r.flagged_reason}</Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize:11,color:T.border }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display:"flex",flexDirection:"column",gap:0.3 }}>
                        <Box sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
                          <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,width:16 }}>R</Typography>
                          <Typography sx={{ fontSize:12,fontWeight:700,color:T.text }}>
                            ₹{Number(r.referrer_amount??0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
                          <Typography sx={{ fontSize:10,fontWeight:700,color:T.muted,width:16 }}>E</Typography>
                          <Typography sx={{ fontSize:12,fontWeight:600,color:T.muted }}>
                            ₹{Number(r.referee_amount??0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display:"flex",justifyContent:"flex-end",gap:0.5 }}>
                        {r.status === "flagged" && (
                          <Tooltip title="Approve (un-flag)">
                            <IconButton size="small" disabled={busyId === r.referral_id}
                              onClick={()=>action(r.referral_id,"approve")}
                              sx={{ color:T.emerald,bgcolor:T.emeraldLt,borderRadius:"8px","&:hover":{ bgcolor:"#a7f3d0" } }}>
                              <CheckCircleRoundedIcon sx={{ fontSize:16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(r.status === "pending" || r.status === "qualifying") && (
                          <Tooltip title="Reject (flag as fraud)">
                            <IconButton size="small" disabled={busyId === r.referral_id}
                              onClick={()=>action(r.referral_id,"reject")}
                              sx={{ color:T.rose,bgcolor:T.roseLt,borderRadius:"8px","&:hover":{ bgcolor:"#fecdd3" } }}>
                              <CancelRoundedIcon sx={{ fontSize:16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {busyId === r.referral_id && (
                          <CircularProgress size={16} sx={{ color:T.teal,ml:0.5,mt:"4px" }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display:"flex",justifyContent:"center",mt:3 }}>
          <Pagination count={totalPages} page={page} onChange={(_,p)=>setPage(p)}
            sx={{ "& .MuiPaginationItem-root":{ borderRadius:"10px" },
                  "& .Mui-selected":{ bgcolor:`${T.teal} !important`,color:"#fff" } }} />
        </Box>
      )}
    </Box>
  );
}
